import {
  ChildConditionType,
  ConditionOperator,
  PropertyConditionName,
  WorkflowScopeType,
} from '@invenco/common-domain/enums';

import {
  EditorConditionGroup,
  EditorConditionLeaf,
  EditorConditionNode,
  EditorRule,
} from '../editor-model';
import { isValuePresent, type FieldInputValue } from '../field-input';
import {
  getPropertyOperators,
  getScopeProperties,
  resolveSubPropertyInput,
  resolveValueInput,
} from '../field-registry';

/** Transforms over a rule's condition tree, composed into `editor.updateRule` recipes. */

/** Stages a recipe onto the rule being edited — the panel's curried `editor.updateRule`. */
export type UpdateRule = (recipe: (rule: EditorRule) => EditorRule) => void;

/**
 * Session-monotonic ids prefixed `new-` so they can't collide with the
 * normalizer's `cond-N` ids or reuse a deleted row's React key.
 */
let nodeIdCounter = 0;
const mintNodeId = (): string => `new-${++nodeIdCounter}`;

const mapLeaf = (
  node: EditorConditionNode,
  id: string,
  fn: (leaf: EditorConditionLeaf) => EditorConditionLeaf,
): EditorConditionNode => {
  if (node.kind === 'leaf') {
    return node.id === id ? fn(node) : node;
  }
  return { ...node, children: node.children.map((child) => mapLeaf(child, id, fn)) };
};

const updateLeaf = (
  rule: EditorRule,
  id: string,
  fn: (leaf: EditorConditionLeaf) => EditorConditionLeaf,
): EditorRule => ({
  ...rule,
  condition: mapLeaf(rule.condition, id, fn) as EditorConditionGroup,
});

const mapGroup = (
  group: EditorConditionGroup,
  id: string,
  fn: (group: EditorConditionGroup) => EditorConditionGroup,
): EditorConditionGroup => {
  if (group.id === id) {
    return fn(group);
  }
  return {
    ...group,
    children: group.children.map((child) =>
      child.kind === 'group' ? mapGroup(child, id, fn) : child,
    ),
  };
};

export const setName = (rule: EditorRule, name: string): EditorRule => ({ ...rule, name });

/** Changing the property resets operator and value — carried-over ones would likely be invalid. */
export const setLeafProperty = (
  rule: EditorRule,
  id: string,
  property: PropertyConditionName,
): EditorRule =>
  updateLeaf(rule, id, (leaf) => ({
    ...leaf,
    property,
    operator: getPropertyOperators(property)[0] ?? ConditionOperator.EQUAL_TO,
    value: undefined,
    subProperty: undefined,
  }));

/**
 * Changing the operator resets the value only when the resolved input kind
 * changes; same-kind swaps keep what the user typed.
 */
export const setLeafOperator = (
  rule: EditorRule,
  id: string,
  operator: ConditionOperator,
): EditorRule =>
  updateLeaf(rule, id, (leaf) => {
    const kindChanged =
      resolveValueInput(leaf.property, leaf.operator).kind !==
      resolveValueInput(leaf.property, operator).kind;
    return { ...leaf, operator, value: kindChanged ? undefined : leaf.value };
  });

export const setLeafValue = (rule: EditorRule, id: string, value: FieldInputValue): EditorRule =>
  updateLeaf(rule, id, (leaf) => ({ ...leaf, value }));

/** Stores the sub-property ("Field"), normalising blank to `undefined` so it's omitted on save. */
export const setLeafSubProperty = (rule: EditorRule, id: string, subProperty: string): EditorRule =>
  updateLeaf(rule, id, (leaf) => ({ ...leaf, subProperty: subProperty || undefined }));

/** A new leaf: the scope's first property, its first operator, no value; born `fresh`. */
export const newLeaf = (scopeType: WorkflowScopeType): EditorConditionLeaf => {
  const property = getScopeProperties(scopeType)[0];
  return {
    kind: 'leaf',
    id: mintNodeId(),
    property,
    operator: getPropertyOperators(property)[0] ?? ConditionOperator.EQUAL_TO,
    value: undefined,
    fresh: true,
  };
};

/** Ends every leaf's grace period — a failed submit reveals the whole rule. */
export const revealLeaves = (rule: EditorRule): EditorRule => {
  const reveal = (node: EditorConditionNode): EditorConditionNode =>
    node.kind === 'leaf'
      ? { ...node, fresh: false }
      : { ...node, children: node.children.map(reveal) };
  return { ...rule, condition: reveal(rule.condition) as EditorConditionGroup };
};

const appendToRoot = (rule: EditorRule, child: EditorConditionNode): EditorRule => ({
  ...rule,
  condition: { ...rule.condition, children: [...rule.condition.children, child] },
});

/** Appends an already-built leaf at the root — stages the empty state's pre-minted row. */
export const addExistingLeaf = (rule: EditorRule, leaf: EditorConditionLeaf): EditorRule =>
  appendToRoot(rule, leaf);

/** Appends a flat leaf row at the root (footer "Add condition"). */
export const addLeaf = (rule: EditorRule, scopeType: WorkflowScopeType): EditorRule =>
  appendToRoot(rule, newLeaf(scopeType));

/**
 * Appends a group at the root, seeded with one empty leaf and taking the
 * opposite connective to the root's (And-of-Ors). Groups never nest further.
 */
export const addGroup = (rule: EditorRule, scopeType: WorkflowScopeType): EditorRule => {
  const group: EditorConditionGroup = {
    kind: 'group',
    id: mintNodeId(),
    connective:
      rule.condition.connective === ChildConditionType.ALL
        ? ChildConditionType.ANY
        : ChildConditionType.ALL,
    children: [newLeaf(scopeType)],
  };
  return appendToRoot(rule, group);
};

/** Appends a leaf row to the group with `groupId` ("+ Condition" inside a group box). */
export const addLeafToGroup = (
  rule: EditorRule,
  groupId: string,
  scopeType: WorkflowScopeType,
): EditorRule => ({
  ...rule,
  condition: mapGroup(rule.condition, groupId, (group) => ({
    ...group,
    children: [...group.children, newLeaf(scopeType)],
  })),
});

/** Every container's child ids in order, keyed by container id (root group included). */
export type TreeOrder = Record<string, string[]>;

/**
 * Rebuilds the condition tree to `order` at any depth. Anything that isn't a
 * global permutation of the existing nodes is stale drag state — the rule is
 * returned unchanged rather than losing nodes. Groups emptied by a move
 * survive until {@link removeEmptyGroups} on drag commit.
 */
export const applyTreeOrder = (rule: EditorRule, order: TreeOrder): EditorRule => {
  const containerIds = new Set<string>([rule.condition.id]);
  const unclaimed = new Map<string, EditorConditionNode>();
  const indexChildren = (group: EditorConditionGroup): void => {
    for (const child of group.children) {
      unclaimed.set(child.id, child);
      if (child.kind === 'group') {
        containerIds.add(child.id);
        indexChildren(child);
      }
    }
  };
  indexChildren(rule.condition);

  for (const containerId of Object.keys(order)) {
    if (!containerIds.has(containerId)) {
      return rule;
    }
  }

  // Claim-once: a duplicated id — including a group inside its own subtree,
  // already claimed by the time its list is built — reads as unknown.
  const build = (containerId: string): EditorConditionNode[] | null => {
    const nodes: EditorConditionNode[] = [];
    for (const id of order[containerId] ?? []) {
      const node = unclaimed.get(id);
      if (!node) {
        return null;
      }
      unclaimed.delete(id);
      if (node.kind !== 'group') {
        nodes.push(node);
        continue;
      }
      const children = build(node.id);
      if (!children) {
        return null;
      }
      nodes.push({ ...node, children });
    }
    return nodes;
  };

  const children = build(rule.condition.id);
  if (!children || unclaimed.size > 0) {
    return rule;
  }
  return { ...rule, condition: { ...rule.condition, children } };
};

/**
 * Drops empty groups at any depth, cascading. Run only on drag commit — a
 * mid-drag sweep would kill the group being dragged out of.
 */
export const removeEmptyGroups = (rule: EditorRule): EditorRule => {
  const sweep = (children: EditorConditionNode[]): EditorConditionNode[] =>
    children.flatMap((child): EditorConditionNode[] => {
      if (child.kind !== 'group') {
        return [child];
      }
      const swept = sweep(child.children);
      return swept.length === 0 ? [] : [{ ...child, children: swept }];
    });
  return { ...rule, condition: { ...rule.condition, children: sweep(rule.condition.children) } };
};

/** Removes the node with `nodeId`; any group left empty is dropped too. */
export const deleteNode = (rule: EditorRule, nodeId: string): EditorRule => {
  const prune = (children: EditorConditionNode[]): EditorConditionNode[] =>
    children.flatMap((child): EditorConditionNode[] => {
      if (child.id === nodeId) {
        return [];
      }
      if (child.kind !== 'group') {
        return [child];
      }
      const pruned = prune(child.children);
      return pruned.length === 0 ? [] : [{ ...child, children: pruned }];
    });

  return { ...rule, condition: { ...rule.condition, children: prune(rule.condition.children) } };
};

/**
 * Complete = value present (unless the operator takes no operand) and
 * sub-property present when the property is key-value.
 */
export const isLeafComplete = (leaf: EditorConditionLeaf): boolean => {
  const valueOk =
    resolveValueInput(leaf.property, leaf.operator).kind === 'none' || isValuePresent(leaf.value);
  const subOk = resolveSubPropertyInput(leaf.property) === null || isValuePresent(leaf.subProperty);
  return valueOk && subOk;
};
